import { Component, Output, EventEmitter } from '@angular/core';
import { OrgService } from './add-customer.service';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { AuthService } from 'src/app/core/services/auth.service';
import { MatCardModule } from '@angular/material/card';
import { Router } from '@angular/router';
import { OrganizationStateService } from '../../../shared/organization-state.service';

@Component({
  selector: 'app-add-customer',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatCardModule],
  templateUrl: './add-customer.component.html',
  styleUrl: './add-customer.component.scss',
})
export class AddCustomerComponent {
  stepForm: FormGroup;
  types = [];
  serverError: any;
  saveSuccess = false;
  isLoading = false;
  private _alertTimeout: any;

  @Output() addOrgSuccess = new EventEmitter<void>();

  constructor(
    private fb: FormBuilder,
    private companySvc: OrgService,
    private authService: AuthService,
    private router: Router,
    private orgState: OrganizationStateService
  ) {}

  ngOnInit(): void {
    this.stepForm = this.fb.group({
      companyType: ['', []], // Use empty string as default for select
      name: [`companyname_${Date.now()}`, [Validators.required]],
      alias: [`displayname_${Date.now()}`],
      country: ['USA'],
      soldTo: [`soldTo_${Date.now()}`],
      shipTo: [`shipTo_${Date.now()}`],
      excludeReferrals: [false],
      active: [true],
      logo: [''],
      // Add all address/contact fields for correct mapping
      address1: [`testAddress1_${Date.now()}`],
      address2: [`testAddress2_${Date.now()}`],
      city: [`testCity_${Date.now()}`],
      state: [`testState_${Date.now()}`],
      postalCode: [`testPostalCode_${Date.now()}`],
      phone: [`testPhone_${Date.now()}`],
      firstName: [`testFirstName_${Date.now()}`],
      lastName: [`testLastName_${Date.now()}`],
    });

    const token = this.authService.token;
    this.companySvc.getOrgTypes(token).subscribe({
      next: (data) => {
        this.types = data.organizationTypes;
      },
      error: (err) => {
        this.serverError = err;
      },
    });
  }

  save() {
    // Mark all controls as touched to trigger validation messages
    this.stepForm.markAllAsTouched();

    if (this.stepForm.invalid) {
      return;
    }

    this.isLoading = true;
    this.serverError = null;
    this.saveSuccess = false;

    const formValue = this.stepForm.value;
    let organizationTypeId = formValue.companyType;
    if (organizationTypeId && typeof organizationTypeId !== 'string') {
      organizationTypeId = organizationTypeId.toString();
    }

    const getAddress = () => ({
      address1: formValue.address1 || 'testAddress1',
      address2: formValue.address2 || 'testAddress2',
      country: formValue.country || 'testCountry',
      city: formValue.city || 'testCity',
      state: formValue.state || 'testState',
      postalCode: formValue.postalCode || '12345',
      phone: formValue.phone || '1234567890',
    });

    const getContactInfo = () => ({
      firstName: formValue.firstName || 'testFirstName',
      lastName: formValue.lastName || 'testLastName',
    });

    const requestBody = {
      name: formValue.name,
      alias: formValue.alias,
      active: formValue.active,
      address: getAddress(),
      contactInfo: getContactInfo(),
      shipTo: formValue.shipTo,
      soldTo: formValue.soldTo,
      clientCertificateVerificationRequired: false,
      excludeReferrals: formValue.excludeReferrals,
      logoObjectKey: formValue.logo || 'string',
      organizationTypeId: organizationTypeId,
    };

    const token = this.authService.token;
    this.companySvc.createOrg(requestBody, token).subscribe({
      next: (response) => {
        this.handleSaveSuccess(response);
      },
      error: (err) => {
        this.handleSaveError(err);
      },
    });
  }

  private handleSaveSuccess(response: any) {
    this.isLoading = false;
    this.saveSuccess = true;
    this.serverError = null;
    this.orgState.setOrgResponse(response);
    if (this._alertTimeout) clearTimeout(this._alertTimeout);
    this._alertTimeout = setTimeout(() => this.dismissAlert(), 4000);
    this.addOrgSuccess.emit();
    // Optionally navigate to sub-org after save
    // this.router.navigate(['/components/organization-management/add-sub-organization']);
  }

  private handleSaveError(err: any) {
    this.isLoading = false;
    this.serverError = err;
    this.saveSuccess = false;
    if (this._alertTimeout) clearTimeout(this._alertTimeout);
    this._alertTimeout = setTimeout(() => this.dismissAlert(), 4000);
  }

  dismissAlert() {
    this.saveSuccess = false;
    this.serverError = null;
    if (this._alertTimeout) {
      clearTimeout(this._alertTimeout);
      this._alertTimeout = null;
    }
  }
}
